import React, { useEffect, useState } from 'react';
import type { DeviceProfile } from '@lumos/contracts';
import { getDeviceProfiles } from '../api/endpoints.js';
import { AuthScene } from '../components/AuthScene.js';

/**
 * Выбор профиля.
 *
 * Показывается при каждом открытии приложения, когда к устройству привязано
 * несколько профилей: телевизор в гостиной и семейный ноутбук смотрят разные
 * люди, и просмотренное одним не должно попадать в «продолжить» другому. Из
 * одного профиля выбирать нечего — тогда экран не появляется вовсе.
 *
 * Что делать с выбором — решает вызывающий: он же знает, кто смотрел до этого,
 * и остаться ли на прежней странице или уйти на главную.
 *
 * Кружки с аватаром и именем под ним — та же форма, что в приложении для
 * телевизора: один сервис, один способ выбрать себя.
 */
export function ProfilePage({ onChoose }: { onChoose: (profile: DeviceProfile) => void }): React.JSX.Element {
  const [profiles, setProfiles] = useState<DeviceProfile[] | null>(null);

  useEffect(() => {
    getDeviceProfiles()
      .then((response) => setProfiles(response.profiles))
      .catch(() => setProfiles([]));
  }, []);

  // Тот же экран, что и вход: форма слева, сцена справа. Это одна и та же
  // дверь — «назовись», — и выглядеть она должна одинаково, а не двумя разными
  // приложениями подряд.
  const card = (content: React.ReactNode): React.JSX.Element => (
    <AuthScene className="profile-card">
      <h1>Кто смотрит?</h1>
      {content}
    </AuthScene>
  );

  if (profiles === null) return card(<p className="state">Загружаем профили…</p>);

  return card(
    <div className="profile-list">
      {profiles.map((profile) => (
        <button
          type="button"
          className="profile-bubble"
          key={profile.id}
          onClick={() => onChoose(profile)}
        >
          <span className="profile-avatar" data-owner={profile.owner}>
            {/* Картинка, если её загрузил администратор; иначе эмодзи, иначе
                инициал — по нему профиль тоже узнаётся. */}
            {profile.avatarUrl !== null ? (
              <img src={profile.avatarUrl} alt="" />
            ) : (
              profile.avatar ?? profile.displayName.slice(0, 1).toUpperCase()
            )}
          </span>
          <span className="profile-name">{profile.displayName}</span>
        </button>
      ))}
    </div>,
  );
}
